import { useEffect, useState } from "react";

import Link from "@material-ui/core/Link";

import { RS } from "../../../../data/strings/global";
import * as SC from "../../../../data/strings/PIStringConst";

import * as Theme from "../../../../app/Theme";

import * as gbtu from "../../../GB/GBTableUtil";
import * as gbtc from "../../../GB/GBTableConst";
import * as gbu from "../../../GB/GBUtil";
import * as pitu from "../../NonComponents/PITableUtil";
import * as pisc from "../../NonComponents/PIServerConst";
import * as piasu from "../../NonComponents/PIAppStateUtil";
import * as piu from "../../NonComponents/PIUtil";

import { generateTypes } from "../../../../utilities";
import SuperTableShim from "../../../common/SuperTableShim";
import { getProjDefaultsAsync } from "../../../../api/server_calls";

const firstRow = 0;
const firstCol = 0;
const oneMonthCol = 1;
const threeMonthCol = 2;
const sixMonthCol = 3;
const twelveMonthCol = 4;
const twentyFourMonthCol = 5;
const sourceCol = 6;
const numCols = sourceCol + 1;

const sources = {
  [pisc.SDCsCCMstID]: {
    title: "Based on Baeten et al., 2016 with estimates for 24 months",
    url: "https://journals.plos.org/plosmedicine/article?id=10.1371/journal.pmed.1002099",
  },
  [pisc.PBWCCMstID]: {
    title: "Based on Davey et al., 2024 with estimates for 24 months",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC13444643/pdf/nihms-2183467.pdf",
  },
  [pisc.KPCCMstID]: {
    title: "Based on Ndenkeh et al., 2022 with estimates for 24 months",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9377496/pdf/qai-91-39.pdf",
  },
  [pisc.RingCCMstID]: {
    title: "Based on Fonner et al., 2025 with estimates for 24 months",
    url: "https://onlinelibrary.wiley.com/doi/10.1002/jia2.26457?msockid=113281a8330564fb01f5948f324a659c",
  },
  [pisc.CAB_LACCMstID]: {
    title: "IAS abstract OAC1904, with estimates for 24 months",
    url: "https://www.iasociety.org/sites/default/files/AIDS2026/AIDS-2026_Abstracts.pdf",
  },
  [pisc.LENCCMstID]: {
    title:
      "Hatzlold, August 2026 - Accelerating LEN Introduction and Scale-Up: Lessons from Nine Early-Adopter Countries",
    url: null,
  },
};

const PIContRefTable = (props) => {
  const { modVarObjList } = props;

  const [loading, setLoading] = useState(true);
  const [defaultCurves, setDefaultCurves] = useState([]);

  useEffect(() => {
    const fetchDefaults = async () => {
      const pseMode = piasu.isPSEMode(modVarObjList);
      const countryCode = piasu.getModVarValue(modVarObjList, "PI_CountryISO");
      const { modvars: defaults } = await getProjDefaultsAsync({ countryCode, pse: pseMode });
      const defCurves = piasu.getModVarValue(defaults, pisc.continuationCurvesMVTag);
      setDefaultCurves(defCurves);
      setLoading(false);
    };

    fetchDefaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <p>Loading...</p>;

  const numRows = defaultCurves.length + 1;

  let packTable = gbtu.getNewPackTable();
  packTable = gbtu.resizePackTable(packTable, numRows, numCols);

  /* Column headings */

  gbtu.setValue(packTable, firstRow, oneMonthCol, RS(SC.GB_stAfterOneMonth) + " (%)");
  gbtu.setValue(packTable, firstRow, threeMonthCol, RS(SC.GB_stAfterThreeMonths) + " (%)");
  gbtu.setValue(packTable, firstRow, sixMonthCol, RS(SC.GB_stAfterSixMonths) + " (%)");
  gbtu.setValue(packTable, firstRow, twelveMonthCol, RS(SC.GB_stAfter12Months) + " (%)");
  gbtu.setValue(packTable, firstRow, twentyFourMonthCol, RS(SC.GB_stAfter24Months) + " (%)");
  gbtu.setValue(packTable, firstRow, sourceCol, RS(SC.GB_stSource));

  /* Rows*/

  let row = 1;
  for (const defaultCurve of defaultCurves) {
    const { [pisc.contCurveMstID]: curveID, [pisc.contCurvePercOnPrEP]: percOnPrep } = defaultCurve;
    const curveName = piu.getDefContCurveNameFromMstID(curveID);
    const [oneMonthVal, threeMonthVal, sixMonthVal, twelveMonthVal, twentyFourMonthVal] = percOnPrep;

    gbtu.setValue(packTable, row, firstCol, curveName);
    gbtu.setValue(packTable, row, oneMonthCol, oneMonthVal);
    gbtu.setValue(packTable, row, threeMonthCol, threeMonthVal);
    gbtu.setValue(packTable, row, sixMonthCol, sixMonthVal);
    gbtu.setValue(packTable, row, twelveMonthCol, twelveMonthVal);
    gbtu.setValue(packTable, row, twentyFourMonthCol, twentyFourMonthVal);
    gbtu.setRowHeight(packTable, row, 60);

    const source = sources[curveID];
    const { title: sourceTitle, url: sourceUrl } = source;
    const linkKey = `link_row_${row}`;

    packTable.components[row][sourceCol] = () => {
      if (sourceUrl) {
        return (
          <Link
            key={linkKey}
            onClick={() => gbu.URL_Click(sourceUrl)}
            style={{
              color: Theme.PI_LinkBlue,
              cursor: "pointer",
            }}
            underline={"hover"}
          >
            {sourceTitle}
          </Link>
        );
      } else {
        return <p key={linkKey}>{sourceTitle}</p>;
      }
    };
    row++;
  }

  gbtu.setColWidth(packTable, firstCol, Theme.itemNameColWidth);
  gbtu.setColWidth(packTable, sourceCol, 300);
  gbtu.alignNumericCellsRight(packTable);
  gbtu.setRowAlignment(packTable, firstRow, gbtc.hAlign.center);
  gbtu.setRDecs(packTable, 1);
  gbtu.setMinAllowedValForTable(packTable, 0);
  gbtu.setMaxAllowedValForTable(packTable, gbtc.maxInt);
  gbtu.setRowHeight(packTable, firstRow, 75);
  gbtu.setWordWrappedCol(packTable, firstCol, true);
  gbtu.lockPackTable(packTable, true, true);

  const types = generateTypes(packTable, ["s", "n", "n", "n", "n", "n", "cm"]);

  return (
    <SuperTableShim
      font={Theme.tableFont}
      headerBackgroundColor={Theme.PI_PrimaryColor}
      key={"contRefTable"}
      oddRowBackgroundColor={Theme.PI_BandColor}
      packTable={packTable}
      types={types}
      onPackTableChanged={() => {}}
      removedMenuNames={pitu.tableHideMenuItems}
      style={{
        tableFont: Theme.tableFont,
        marginTop: Theme.ctrlSpacing,
        padding: 0,
      }}
    />
  );
};

export default PIContRefTable;
